import React, { useMemo, useState } from "react";
import { Html } from "@react-three/drei";
import * as THREE from "three";
import type { ThermalModelConfig } from "../../../../../../../Missions/Satellites/SatelliteProfile/SatelliteDesigner/ThermalDesigner/types/ThermalConfig";
import {
    AXES, SHAFT_LEN, HEAD_LEN, HEAD_RAD, SHAFT_RAD, FaceId, FACE_CONFIGS, tempToHex,
    SOLAR_PANEL_SIZE, SOLAR_PANEL_X, SOLAR_PANEL_Y_OFFSET, SOLAR_LAYER_GAP, SOLAR_CELLS_PER_SIDE, SOLAR_CELL_EDGE_MARGIN,
    SOLAR_CELL_FILL_RATIO, SOLAR_PANEL_BASE_COLOR, SOLAR_CELL_COLOR_DARK, SOLAR_CELL_COLOR_LIGHT,
    SOLAR_PANEL_OUTLINE_COLOR
} from "../../constants/thermalConstants";
import { useVertexColors, useCellPositions, useGridLinesGeo } from "../../hooks/CubeAndFaceHooks";
import ThermalSubsystemRenderer from "./ThermalSubsystemRenderer";
import { SUBSYSTEM_LABELS_BY_NODE } from "./thermalHeatmapSubsystemConfig";

// ---------------------------------------------------------------------------
// Cube wireframe + axes
// ---------------------------------------------------------------------------

export const CubeWireframe: React.FC = () => {
    const geo = useMemo(() => new THREE.EdgesGeometry(new THREE.BoxGeometry(1, 1, 1)), []);
    return (
        <lineSegments geometry={geo}>
            <lineBasicMaterial color="#999999" transparent opacity={0.5} />
        </lineSegments>
    );
};

// Memoized axis geometries to avoid recreating them every render
const cylinderGeo = new THREE.CylinderGeometry(SHAFT_RAD, SHAFT_RAD, SHAFT_LEN, 8);
const coneGeo = new THREE.ConeGeometry(HEAD_RAD, HEAD_LEN, 8);

export interface CubeAxesProps {
    showNadir?: boolean;
    showRollPitchYaw?: boolean;
}

export const CubeAxes: React.FC<CubeAxesProps> = ({ showNadir = true, showRollPitchYaw = true }) => {
    const axisRenderData = useMemo(() => AXES
        .filter(({ label }) => {
            if (label === "X") return showNadir || showRollPitchYaw;
            return showRollPitchYaw;
        })
        .map(({ dir, color, label, annotation }) => {
            const isNadirOnly = label === "X" && showNadir && !showRollPitchYaw;
            const [dx, dy, dz] = dir;
            const conePosP:  [number, number, number] = [dx * (SHAFT_LEN / 2 + HEAD_LEN / 2),  dy * (SHAFT_LEN / 2 + HEAD_LEN / 2),  dz * (SHAFT_LEN / 2 + HEAD_LEN / 2)];
            const labelPosP: [number, number, number] = [dx * (SHAFT_LEN / 2 + HEAD_LEN + 0.07), dy * (SHAFT_LEN / 2 + HEAD_LEN + 0.07), dz * (SHAFT_LEN / 2 + HEAD_LEN + 0.07)];
            const conePosN:  [number, number, number] = [-dx * (SHAFT_LEN / 2 + HEAD_LEN / 2), -dy * (SHAFT_LEN / 2 + HEAD_LEN / 2), -dz * (SHAFT_LEN / 2 + HEAD_LEN / 2)];
            const labelPosN: [number, number, number] = [-dx * (SHAFT_LEN / 2 + HEAD_LEN + 0.07), -dy * (SHAFT_LEN / 2 + HEAD_LEN + 0.07), -dz * (SHAFT_LEN / 2 + HEAD_LEN + 0.07)];
            const quatP = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), new THREE.Vector3(dx, dy, dz));
            const rotP  = new THREE.Euler().setFromQuaternion(quatP);
            const rotationP: [number, number, number] = [rotP.x, rotP.y, rotP.z];
            const quatN = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), new THREE.Vector3(-dx, -dy, -dz));
            const rotN  = new THREE.Euler().setFromQuaternion(quatN);
            const rotationN: [number, number, number] = [rotN.x, rotN.y, rotN.z];
            const labelStyle: React.CSSProperties = { color, fontSize: 10, fontWeight: 700, fontFamily: "Montserrat, sans-serif", userSelect: "none", pointerEvents: "none" };
            return { label, color, annotation, isNadirOnly, conePosP, labelPosP, conePosN, labelPosN, rotationP, rotationN, labelStyle };
        }), [showNadir, showRollPitchYaw]);

    return (
        <group>
            {axisRenderData.map(({ label, color, isNadirOnly, conePosP, labelPosP, conePosN, labelPosN, rotationP, rotationN, labelStyle, annotation }) => (
                <group key={label}>
                    <mesh geometry={cylinderGeo} rotation={rotationP}>
                        <meshBasicMaterial color={color} />
                    </mesh>
                    <mesh geometry={coneGeo} position={conePosP} rotation={rotationP}>
                        <meshBasicMaterial color={color} />
                    </mesh>
                    <Html center position={labelPosP} zIndexRange={[10, 0]}>
                        {isNadirOnly
                            ? <span style={labelStyle}>Nadir</span>
                            : <span style={labelStyle}>+{label}<span style={{ fontSize: 7, fontWeight: 400, marginLeft: 2 }}>({annotation})</span></span>
                        }
                    </Html>
                    <mesh geometry={coneGeo} position={conePosN} rotation={rotationN}>
                        <meshBasicMaterial color={color} />
                    </mesh>
                    <Html center position={labelPosN} zIndexRange={[10, 0]}>
                        <span style={isNadirOnly ? { ...labelStyle, fontSize: 7, whiteSpace: "nowrap" } : labelStyle}>{isNadirOnly ? "Sun Pointing" : `−${label}`}</span>
                    </Html>
                </group>
            ))}
        </group>
    );
};

// ---------------------------------------------------------------------------
// Face cells + blended face with hover node temperatures
// ---------------------------------------------------------------------------

export interface FaceCellsProps {
    faceId: FaceId;
    nodes: Record<string, number>;
    nodesPerFace: number;
    globalMin: number;
    globalMax: number;
    showNodeTemp?: boolean;
}

// Ghost variant – light-blue transparent fill when Internal Faces layer is on
export const FaceGhost: React.FC<{ faceId: FaceId }> = ({ faceId }) => {
    const { rotation, offset } = FACE_CONFIGS[faceId];
    const pos: [number, number, number] = [offset[0] * 1.001, offset[1] * 1.001, offset[2] * 1.001];
    return (
        <mesh rotation={rotation} position={pos}>
            <planeGeometry args={[1, 1]} />
            <meshBasicMaterial color="#204D69" transparent opacity={0.08} side={THREE.DoubleSide} depthWrite={false} />
        </mesh>
    );
};

// ---------------------------------------------------------------------------
// Shared: hover cells component
// ---------------------------------------------------------------------------
const NodeHoverCells: React.FC<{
    cellPositions: { ni: number; x: number; y: number }[];
    cellSize: number;
    nodes: Record<string, number>;
    keyPrefix: string;
    /** Mapped subsystem names read “{subsystems}: {temp}°C”; unmapped nodes read “{title} N{n}: {temp}°C” when provided, else “{temp}°C” */
    title?: string;
}> = ({ cellPositions, cellSize, nodes, keyPrefix, title }) => {
    const [hoveredNi, setHoveredNi] = useState<number | null>(null);
    return (
        <>
            {cellPositions.map(({ ni, x, y }) => (
                <mesh
                    key={ni}
                    position={[x, y, 0.003]}
                    onPointerEnter={(e) => { e.stopPropagation(); setHoveredNi(ni); }}
                    onPointerLeave={() => setHoveredNi(null)}
                >
                    <planeGeometry args={[cellSize, cellSize]} />
                    <meshBasicMaterial transparent opacity={0} depthWrite={false} />
                </mesh>
            ))}
            {hoveredNi !== null && (() => {
                const { x, y } = cellPositions[hoveredNi];
                const nodeKey = `${keyPrefix}_N${hoveredNi + 1}`;
                const temp = (nodes[nodeKey] ?? 0).toFixed(2);
                const subsystemLabel = SUBSYSTEM_LABELS_BY_NODE[nodeKey];
                const label = subsystemLabel
                    ? `${subsystemLabel}: ${temp}°C`
                    : title ? `${title} N${hoveredNi + 1}: ${temp}°C` : `${temp}°C`;
                return (
                    <Html position={[x, y, 0.004]} center style={{ pointerEvents: "none" }}>
                        <div style={{
                            color: "#fff",
                            fontFamily: "Montserrat, sans-serif",
                            fontSize: 9,
                            fontWeight: 700,
                            whiteSpace: "nowrap",
                            textShadow: "0 0 3px #000, 0 0 3px #000",
                            userSelect: "none",
                            pointerEvents: "none",
                        }}>{label}</div>
                    </Html>
                );
            })()}
        </>
    );
};

// ---------------------------------------------------------------------------
// Internal deck planes (Deck 1 = lower, Deck 2 = upper)
// ---------------------------------------------------------------------------

const INTERNAL_DECK_Y    = 1 / 6;
const INTERNAL_DECK_SIZE = 0.98;
const DECK_ROTATION: [number, number, number] = [-Math.PI / 2, 0, 0];
// Static outline geometry — INTERNAL_DECK_SIZE never changes
const OUTLINE_GEO = new THREE.EdgesGeometry(new THREE.PlaneGeometry(INTERNAL_DECK_SIZE, INTERNAL_DECK_SIZE));

export interface InternalDecksHeatmapProps {
    nodes: Record<string, number>;
    nodesPerFace: number;
    globalMin: number;
    globalMax: number;
    showNodeTemp?: boolean;
    showGrid?: boolean;
    showSubsystems?: boolean;
    thermalModel?: ThermalModelConfig;
}

const InternalDeckPlane: React.FC<{
    deckKey: string;
    label: string;
    nodes: Record<string, number>;
    nodesPerFace: number;
    globalMin: number;
    globalMax: number;
    showNodeTemp?: boolean;
    showGrid?: boolean;
}> = ({ deckKey, label, nodes, nodesPerFace, globalMin, globalMax, showNodeTemp, showGrid }) => {
    const gridSize = Math.round(Math.sqrt(nodesPerFace));
    const cellSize = INTERNAL_DECK_SIZE / gridSize;
    const geometry = useVertexColors(gridSize, INTERNAL_DECK_SIZE, nodes, deckKey, globalMin, globalMax);
    const hasData  = nodes[`${deckKey}_N1`] !== undefined;
    const cellPositions = useCellPositions(nodesPerFace, gridSize, cellSize);
    const innerGridGeo  = useGridLinesGeo(gridSize, INTERNAL_DECK_SIZE, false);

    return (
        <>
            {hasData ? (
                <mesh geometry={geometry}>
                    <meshBasicMaterial vertexColors transparent opacity={1} side={THREE.DoubleSide} toneMapped={false} depthWrite={false} />
                </mesh>
            ) : (
                <mesh>
                    <planeGeometry args={[INTERNAL_DECK_SIZE, INTERNAL_DECK_SIZE]} />
                    <meshBasicMaterial color="#4B84A9" transparent opacity={0.22} side={THREE.DoubleSide} depthWrite={false} />
                </mesh>
            )}
            <lineSegments geometry={OUTLINE_GEO}>
                <lineBasicMaterial color="#8BD0FC" transparent opacity={0.25} />
            </lineSegments>
            {showGrid && innerGridGeo && (
                <lineSegments geometry={innerGridGeo}>
                    <lineBasicMaterial color="#000000" transparent opacity={0.3} depthWrite={false} />
                </lineSegments>
            )}
            {showNodeTemp && hasData && (
                <NodeHoverCells cellPositions={cellPositions} cellSize={cellSize} nodes={nodes} keyPrefix={deckKey} title={label} />
            )}
        </>
    );
};

export const InternalDecksHeatmap: React.FC<InternalDecksHeatmapProps> = React.memo(({ 
    nodes, 
    nodesPerFace, 
    globalMin, 
    globalMax, 
    showNodeTemp, 
    showGrid,
    showSubsystems = false,
    thermalModel,
}) => {
    return (
        <group>
            {/* Internal Deck 1 (lower) */}
            <group position={[0, -INTERNAL_DECK_Y, 0]} rotation={DECK_ROTATION}>
                <InternalDeckPlane deckKey="INT1" label="Deck 1" nodes={nodes} nodesPerFace={nodesPerFace} globalMin={globalMin} globalMax={globalMax} showNodeTemp={showNodeTemp} showGrid={showGrid} />
                {/* Subsystems on Deck 1 */}
                {showSubsystems && (
                    <ThermalSubsystemRenderer
                        deckKey="INT1"
                        currentRow={nodes}
                        globalMin={globalMin}
                        globalMax={globalMax}
                        thermalModel={thermalModel}
                    />
                )}
            </group>
            
            {/* Internal Deck 2 (upper) */}
            <group position={[0, INTERNAL_DECK_Y, 0]} rotation={DECK_ROTATION}>
                <InternalDeckPlane deckKey="INT2" label="Deck 2" nodes={nodes} nodesPerFace={nodesPerFace} globalMin={globalMin} globalMax={globalMax} showNodeTemp={showNodeTemp} showGrid={showGrid} />
                {/* Subsystems on Deck 2 */}
                {showSubsystems && (
                    <ThermalSubsystemRenderer
                        deckKey="INT2"
                        currentRow={nodes}
                        globalMin={globalMin}
                        globalMax={globalMax}
                        thermalModel={thermalModel}
                    />
                )}
            </group>
            
            {/* Bottom Face (+X) and Top Face (-X) */}
            <FaceBlend faceId="PX" nodes={nodes} nodesPerFace={nodesPerFace} globalMin={globalMin} globalMax={globalMax} showNodeTemp={showNodeTemp} />
            <FaceBlend faceId="NX" nodes={nodes} nodesPerFace={nodesPerFace} globalMin={globalMin} globalMax={globalMax} showNodeTemp={showNodeTemp} />
            
            {/* Subsystems on +X face (deck-0) */}
            {showSubsystems && (
                <group position={[0, -0.5, 0]} rotation={[Math.PI / 2, 0, 0]}>
                    <ThermalSubsystemRenderer
                        deckKey="PX"
                        currentRow={nodes}
                        globalMin={globalMin}
                        globalMax={globalMax}
                        thermalModel={thermalModel}
                    />
                </group>
            )}
            
            {/* Subsystems on -X face (deck-3) */}
            {showSubsystems && (
                <group position={[0, 0.5, 0]} rotation={[-Math.PI / 2, 0, 0]}>
                    <ThermalSubsystemRenderer
                        deckKey="NX"
                        currentRow={nodes}
                        globalMin={globalMin}
                        globalMax={globalMax}
                        thermalModel={thermalModel}
                    />
                </group>
            )}
            
            {showGrid && (
                <>
                    <FaceGrid faceId="PX" nodesPerFace={nodesPerFace} />
                    <FaceGrid faceId="NX" nodesPerFace={nodesPerFace} />
                </>
            )}
        </group>
    );
});

// ---------------------------------------------------------------------------
/** Black 3×3 (or n×n) grid lines drawn on a face, matching the thermal-designer cube grid. */
export const FaceGrid: React.FC<{ faceId: FaceId; nodesPerFace: number }> = ({ faceId, nodesPerFace }) => {
    const gridSize = Math.round(Math.sqrt(nodesPerFace));
    const { rotation, offset } = FACE_CONFIGS[faceId];
    const pos: [number, number, number] = [offset[0] * 1.003, offset[1] * 1.003, offset[2] * 1.003];
    const geo = useGridLinesGeo(gridSize, 1, true);

    if (!geo) return null;
    return (
        <group rotation={rotation} position={pos}>
            {/* Render grid on both sides with slight z-offset */}
            <lineSegments geometry={geo} position={[0, 0, 0.001]}>
                <lineBasicMaterial color="#000000" transparent opacity={0.2} depthWrite={false} />
            </lineSegments>
            <lineSegments geometry={geo} position={[0, 0, -0.001]}>
                <lineBasicMaterial color="#000000" transparent opacity={0.2} depthWrite={false} />
            </lineSegments>
        </group>
    );
};

// FaceBlend — vertex-coloured face with per-node hover temperatures
// ---------------------------------------------------------------------------

export const FaceBlend: React.FC<FaceCellsProps> = React.memo(({ faceId, nodes, nodesPerFace, globalMin, globalMax, showNodeTemp }) => {
    const gridSize = Math.round(Math.sqrt(nodesPerFace));
    const cellSize = 1 / gridSize;
    const { rotation, offset } = FACE_CONFIGS[faceId];
    const pos: [number, number, number] = [offset[0] * 1.001, offset[1] * 1.001, offset[2] * 1.001];
    const geometry = useVertexColors(gridSize, 1, nodes, faceId, globalMin, globalMax);
    const cellPositions = useCellPositions(nodesPerFace, gridSize, cellSize);

    // Flip Y coordinate for external faces to match internal deck layout (opposite rotations)
    const flippedCellPositions = useMemo(() => {
        if (!showNodeTemp) return cellPositions;
        return (faceId === "PX" || faceId === "NX") ? cellPositions.map(p => ({ ...p, y: -p.y })) : cellPositions;
    }, [faceId, cellPositions, showNodeTemp]);

    return (
        <group rotation={rotation} position={pos}>
            <mesh geometry={geometry}>
                <meshBasicMaterial vertexColors side={THREE.DoubleSide} toneMapped={false} transparent depthWrite={false} opacity={1} />
            </mesh>
            {showNodeTemp && (
                <NodeHoverCells cellPositions={flippedCellPositions} cellSize={cellSize} nodes={nodes} keyPrefix={faceId} />
            )}
        </group>
    );
});

const DECK_LABELS = [
    { label: "Deck 0", y: -0.5 },
    { label: "Deck 1", y: -INTERNAL_DECK_Y },
    { label: "Deck 2", y: INTERNAL_DECK_Y },
    { label: "Deck 3", y: 0.5 },
];

const DECK_LABEL_STYLE: React.CSSProperties = {
    color: "#f0f0f0",
    fontSize: 7,
    fontWeight: 700,
    fontFamily: "Montserrat, sans-serif",
    userSelect: "none",
    pointerEvents: "none",
    textShadow: "0 0 2px #000",
    whiteSpace: "nowrap",
};

export const DeckLabels: React.FC = () => {
    const xOffset = 0.65;

    return (
        <group>
            {DECK_LABELS.map(({ label, y }) => (
                <Html key={label} position={[xOffset, y, 0]} center style={{ pointerEvents: "none" }} zIndexRange={[10, 0]}>
                    <div style={DECK_LABEL_STYLE}>{label}</div>
                </Html>
            ))}
        </group>
    );
};

// ---------------------------------------------------------------------------
// Solar Panels (8×8 cell grid, two panels at x=±1 level with top face)
// ---------------------------------------------------------------------------

/**
 * SolarPanels renders two flat solar panels with an 8×8 cell grid on both sides.
 * Each panel is 1×1, positioned at x=±1, y=0.5, flush with the top (NX) face.
 *
 * Cells are visible from both above and below via:
 * - Top cells at z=+SOLAR_LAYER_GAP, FrontSide (visible from above)
 * - Bottom cells at z=-SOLAR_LAYER_GAP, BackSide (visible from below)
 *
 * Jitter is eliminated by separating layers along the panel normal (z-axis in local coords):
 * - Base plane (opaque, z=0) renders first with depth writes
 * - Cell meshes (opaque, ±SOLAR_LAYER_GAP) render with depth writes
 * - Sheen layers (transparent, ±2*SOLAR_LAYER_GAP) render last without depth writes
 * This avoids z-fighting (identical depth conflicts) that caused flickering.
 */
export const SolarPanels: React.FC = React.memo(() => {
    // Outline geometry: edges of a 1×1 plane
    const outlineGeo = useMemo(() => new THREE.EdgesGeometry(new THREE.PlaneGeometry(SOLAR_PANEL_SIZE, SOLAR_PANEL_SIZE)), []);

    // Cell grid: 8×8 evenly spaced square cells with vertex colors for gradient
    const cellGridGeo = useMemo(() => {
        const cellsPerSide = SOLAR_CELLS_PER_SIDE; // 8
        const panelSize = SOLAR_PANEL_SIZE;        // 1
        const margin = SOLAR_CELL_EDGE_MARGIN;     // 0.05
        const fillRatio = SOLAR_CELL_FILL_RATIO;   // 0.85

        // Usable area within the panel (margin on all sides)
        const usableSize = panelSize - 2 * margin; // 0.9
        const step = usableSize / cellsPerSide;    // 0.1125 per cell pitch
        const cellSize = step * fillRatio;          // ~0.095 per cell
        const start = -panelSize / 2 + margin;     // -0.45

        // Convert hex to RGB [0, 1]
        const hexToRgb = (hex: string): [number, number, number] => {
            const h = hex.replace("#", "");
            return [parseInt(h.slice(0, 2), 16) / 255, parseInt(h.slice(2, 4), 16) / 255, parseInt(h.slice(4, 6), 16) / 255];
        };

        const darkRgb = hexToRgb(SOLAR_CELL_COLOR_DARK);
        const lightRgb = hexToRgb(SOLAR_CELL_COLOR_LIGHT);

        const positions: number[] = [];
        const colors: number[] = [];
        const indices: number[] = [];
        let vertexIndex = 0;

        // Build indexed vertices: each cell uses 4 unique corner vertices
        for (let row = 0; row < cellsPerSide; row++) {
            for (let col = 0; col < cellsPerSide; col++) {
                const x0 = start + col * step;
                const y0 = start + row * step;
                const x1 = x0 + cellSize;
                const y1 = y0 + cellSize;

                // Blend colors based on position (gradient from corners to centre)
                const distFromEdge = Math.min(col, cellsPerSide - 1 - col, row, cellsPerSide - 1 - row);
                const t = Math.min(1, distFromEdge / (cellsPerSide / 4)); // 0 at edges, 1 at centre
                const r = darkRgb[0] + (lightRgb[0] - darkRgb[0]) * t;
                const g = darkRgb[1] + (lightRgb[1] - darkRgb[1]) * t;
                const b = darkRgb[2] + (lightRgb[2] - darkRgb[2]) * t;

                // Add 4 corners as unique vertices
                positions.push(x0, y0, 0);
                colors.push(r, g, b);
                const idx0 = vertexIndex++;

                positions.push(x1, y0, 0);
                colors.push(r, g, b);
                const idx1 = vertexIndex++;

                positions.push(x1, y1, 0);
                colors.push(r, g, b);
                const idx2 = vertexIndex++;

                positions.push(x0, y1, 0);
                colors.push(r, g, b);
                const idx3 = vertexIndex++;

                // Two triangles with consistent winding (CCW when viewed from +Z)
                indices.push(idx0, idx1, idx2);
                indices.push(idx0, idx2, idx3);
            }
        }

        const geo = new THREE.BufferGeometry();
        geo.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
        geo.setAttribute("color", new THREE.Float32BufferAttribute(colors, 3));
        geo.setIndex(new THREE.BufferAttribute(new Uint32Array(indices), 1));
        return geo;
    }, []);

    // Faint sheen plane: overlaid for shine effect (no normals needed for basic material)
    const sheenGeo = useMemo(() => {
        const positions = [
            -SOLAR_PANEL_SIZE / 2, -SOLAR_PANEL_SIZE / 2, 0,
            SOLAR_PANEL_SIZE / 2, -SOLAR_PANEL_SIZE / 2, 0,
            SOLAR_PANEL_SIZE / 2, SOLAR_PANEL_SIZE / 2, 0,
            -SOLAR_PANEL_SIZE / 2, SOLAR_PANEL_SIZE / 2, 0,
        ];
        const indices = [0, 1, 2, 0, 2, 3];
        const geo = new THREE.BufferGeometry();
        geo.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
        geo.setIndex(new THREE.BufferAttribute(new Uint16Array(indices), 1));
        return geo;
    }, []);

    return (
        <>
            {([-SOLAR_PANEL_X, SOLAR_PANEL_X] as const).map((x) => (
                <group key={x} position={[x, SOLAR_PANEL_Y_OFFSET, 0]} rotation={[-Math.PI / 2, 0, 0]}>
                    {/* Base plane: white background, z=0, opaque with depth write */}
                    <mesh position={[0, 0, 0]}>
                        <planeGeometry args={[SOLAR_PANEL_SIZE, SOLAR_PANEL_SIZE]} />
                        <meshBasicMaterial color={SOLAR_PANEL_BASE_COLOR} transparent={false} opacity={1} side={THREE.DoubleSide} polygonOffset polygonOffsetFactor={1} polygonOffsetUnits={1} />
                    </mesh>

                    {/* Top cells: visible from above, z=+SOLAR_LAYER_GAP, FrontSide only, opaque */}
                    <mesh geometry={cellGridGeo} position={[0, 0, SOLAR_LAYER_GAP]}>
                        <meshBasicMaterial vertexColors transparent={false} opacity={1} side={THREE.FrontSide} />
                    </mesh>

                    {/* Bottom cells: visible from below, z=-SOLAR_LAYER_GAP, BackSide only, opaque */}
                    <mesh geometry={cellGridGeo} position={[0, 0, -SOLAR_LAYER_GAP]}>
                        <meshBasicMaterial vertexColors transparent={false} opacity={1} side={THREE.BackSide} />
                    </mesh>

                    {/* Top sheen: transparent, z=+2*SOLAR_LAYER_GAP, renderOrder high, no depth write */}
                    <mesh geometry={sheenGeo} position={[0, 0, 2 * SOLAR_LAYER_GAP]} renderOrder={10}>
                        <meshBasicMaterial color={SOLAR_CELL_COLOR_LIGHT} transparent opacity={0.15} depthWrite={false} side={THREE.FrontSide} />
                    </mesh>

                    {/* Bottom sheen: transparent, z=-2*SOLAR_LAYER_GAP, renderOrder high, no depth write */}
                    <mesh geometry={sheenGeo} position={[0, 0, -2 * SOLAR_LAYER_GAP]} renderOrder={10}>
                        <meshBasicMaterial color={SOLAR_CELL_COLOR_LIGHT} transparent opacity={0.15} depthWrite={false} side={THREE.BackSide} />
                    </mesh>

                    {/* Panel outline: z=0, renderOrder above base */}
                    <lineSegments geometry={outlineGeo} position={[0, 0, 0]} renderOrder={5}>
                        <lineBasicMaterial color={SOLAR_PANEL_OUTLINE_COLOR} transparent opacity={0.7} linewidth={1} />
                    </lineSegments>
                </group>
            ))}
        </>
    );
});
